import { MAX_ZOOM, MIN_ZOOM, type Rect, type Vec2 } from '@vp/core';

export interface CameraState {
  /** World point at the viewport center (float64). */
  cx: number;
  cy: number;
  /** CSS pixels per world unit. */
  zoom: number;
  /** Viewport size in CSS pixels. */
  width: number;
  height: number;
  dpr: number;
}

/** Viewport camera. All math in float64; only small view-relative values go to the GPU (§4.2). */
export class Camera implements CameraState {
  cx = 0;
  cy = 0;
  zoom = 1;
  width = 1;
  height = 1;
  dpr = 1;
  private listeners = new Set<() => void>();

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  state(): CameraState {
    return { cx: this.cx, cy: this.cy, zoom: this.zoom, width: this.width, height: this.height, dpr: this.dpr };
  }

  set(s: Partial<Pick<CameraState, 'cx' | 'cy' | 'zoom'>>): void {
    if (s.cx !== undefined && Number.isFinite(s.cx)) this.cx = s.cx;
    if (s.cy !== undefined && Number.isFinite(s.cy)) this.cy = s.cy;
    if (s.zoom !== undefined) this.zoom = clampZoom(s.zoom);
    this.emit();
  }

  resize(width: number, height: number, dpr: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.dpr = dpr;
    this.emit();
  }

  /** Screen (CSS px, relative to the canvas element) → world. */
  screenToWorld(sx: number, sy: number): Vec2 {
    return { x: this.cx + (sx - this.width / 2) / this.zoom, y: this.cy + (sy - this.height / 2) / this.zoom };
  }

  worldToScreen(wx: number, wy: number): Vec2 {
    return { x: (wx - this.cx) * this.zoom + this.width / 2, y: (wy - this.cy) * this.zoom + this.height / 2 };
  }

  /** World rectangle currently visible. */
  visibleRect(margin = 0): Rect {
    const w = this.width / this.zoom;
    const h = this.height / this.zoom;
    const m = margin / this.zoom;
    return { x: this.cx - w / 2 - m, y: this.cy - h / 2 - m, w: w + 2 * m, h: h + 2 * m };
  }

  /** Pan by screen delta (CSS px). */
  panBy(dx: number, dy: number): void {
    this.cx -= dx / this.zoom;
    this.cy -= dy / this.zoom;
    this.emit();
  }

  /** Zoom keeping the world point under (sx, sy) fixed. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.screenToWorld(sx, sy);
    this.zoom = clampZoom(this.zoom * factor);
    const after = this.screenToWorld(sx, sy);
    this.cx += before.x - after.x;
    this.cy += before.y - after.y;
    this.emit();
  }

  /** Fit a world rectangle into the viewport with padding (CSS px). */
  fit(r: Rect, padding = 48, maxZoom = 1): void {
    const w = Math.max(r.w, 1e-9);
    const h = Math.max(r.h, 1e-9);
    // small viewports: the padding must not eat the whole screen
    padding = Math.min(padding, this.width * 0.15, this.height * 0.15);
    const zx = (this.width - padding * 2) / w;
    const zy = (this.height - padding * 2) / h;
    this.zoom = clampZoom(Math.min(zx, zy, maxZoom));
    this.cx = r.x + r.w / 2;
    this.cy = r.y + r.h / 2;
    this.emit();
  }
}

export function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}
